<template>
  <span class="keyboard-input-container">
    <b-form-textarea
      v-if="type == 'textarea'"
      :value="input"
      @input="onInputChange"
      @focus="onInputFocus"
      @blur="onInputBlur"
      class="keyboard-input"
      :disabled="disabled"
      :required="required"
      :placeholder="placeholder"
      :name="name"
      :autocomplete="autocomplete"
      :id="inputId"
      :class="inputClass"
      :inputMask="inputMask"
    />
    <b-form-input
      v-else
      :value="input"
      @input="onInputChange"
      @focus="onInputFocus"
      @blur="onInputBlur"
      class="keyboard-input"
      :disabled="disabled"
      :required="required"
      :placeholder="placeholder"
      :name="name"
      :autocomplete="autocomplete"
      :type="type || 'text'"
      :id="inputId"
      :class="inputClass"
      :inputMask="inputMask"
    />
    <div :class="keyboardId"></div>
    <small v-if="disableVirtualKeyboard && inputMask"><small>Ex.: {{ inputMask }}</small></small>
	</span>
</template>

<script>
/**
 * SimpleKeyboard for Vue.js
 * 
 * <SimpleKeyboard
 *  v-model=""
 *  type="text|password|email|..."
 *  placeholder=""
 *  name=""
 *  disabled=true|false
 *  required=true|false
 *  autocomplete="on|off"
 *  inputClass=""
 *  options={ layout:{}, layoutName:"", display:{}, theme:"", ... }
 *  inputMask=""
 * />
 * 
 * Options: https://franciscohodge.com/projects/simple-keyboard/documentation/
 */
import Keyboard from "simple-keyboard";
import inputMaskModule from "simple-keyboard-input-mask";
import "simple-keyboard/build/css/index.css";

export default {
  name: 'SimpleKeyboard',
  props: ['value', 'inputClass', 'type', 'placeholder', 'disabled', 'name', 'required', 'autocomplete', 'options', 'inputMask', 'disableVirtualKeyboard'],
  data () {
    return {
      inputId: `simple-keyboard-input-${this._uid}`,
      keyboardId: `simple-keyboard-${this._uid}`,
      input: '',
      keyboard: null
    }
  },
  watch: {
    value: function(v) { // v-model foi atualizado
      if (v !== this.input) {
        // atualiza com o valor do v-model
        this.input = v
        if (this.disableVirtualKeyboard) return
        this.keyboard.setInput(this.input)
      }
    }
  },
  mounted() {
    let defaultOptions = {
      theme: "simple-keyboard hg-theme-default hg-layout-default",
      disableCaretPositioning: true
    }

    let immutableOptions = {
      onChange: this.onKeyboardChange,
      onKeyPress: this.onKeyboardKeyPress,
      inputName: `input-${this._uid}`
    }

    if (this.options && this.options.addTheme) {
      defaultOptions.theme = `${defaultOptions.theme} ${this.options.addTheme}`
    }

    if (this.inputMask) {
      immutableOptions.inputMask = this.inputMask
      immutableOptions.modules = [ inputMaskModule ]
    }

    if (!this.disableVirtualKeyboard) {
      this.keyboard = new Keyboard(`.${this.keyboardId}`, { ...defaultOptions,  ...this.options, ...immutableOptions })
    }

    // atualiza com o valor do v-model
    this.input = this.value
    if (!this.disableVirtualKeyboard) {
      this.keyboard.setInput(this.input)

      // fecha o teclado
      document.addEventListener("click", event => {
        if (
          // Target is not keyboard element
          event.target !== this.keyboard.keyboardDOM &&
          // Target is not the input
          event.target !== document.querySelector(`#${this.inputId}`) &&
          // Target is not a keyboard button
          !event.target.classList.contains("hg-button")
        ) {
          this.closeKeyboard()
        }
      })
    }

  },
  methods: {
    openKeyboard () {
      if (this.disableVirtualKeyboard) return
      this.keyboard.keyboardDOM.classList.add("show-keyboard")
      this.$emit('open') //emite o evento 'open'
    },
    closeKeyboard () {
      if (this.disableVirtualKeyboard) return
      this.keyboard.keyboardDOM.classList.remove("show-keyboard")
      this.$emit('close') //emite o evento 'close'
    },
    onKeyboardKeyPress(button) {
      if (this.disableVirtualKeyboard) return
      if (this.options && this.options.onKeyPress && typeof this.options.onKeyPress === 'function') {
        this.options.onKeyPress(button, this.keyboard)
      }

      if (button === "{close}") {
        this.closeKeyboard()
      }

      if (button === "{clear}") {
        this.onKeyboardChange('')
        this.keyboard.clearInput()
      }

      if (button === "{shift}") {
        this.keyboard.setOptions({
          layoutName: this.keyboard.options.layoutName === "default" ? "shift" : "default"
        })
        this.keyboard.keyboardDOM.classList.add("show-keyboard")
      }

      if (button === "{alt}") {
        this.keyboard.setOptions({
          layoutName: this.keyboard.options.layoutName === "default" ? "alt" : "default"
        })
        this.keyboard.keyboardDOM.classList.add("show-keyboard")
      }
    },
    onKeyboardChange(input) {
      this.input = input //atualiza o value do input
      this.$emit('input', input) //atualiza o v-model
      this.$emit('change', input) //emite o evento 'change'

      if (this.options && this.options.onChange && typeof this.options.onChange === 'function') {
        this.options.onChange(input)
      }
    },
    onInputChange(e) {
      let input = ''
      if (e && e.constructor && e.constructor.name && e.constructor.name === 'InputEvent') {
        input = e.target.value
      } else {
        input = e
      }

      if (!this.disableVirtualKeyboard) {
        this.keyboard.setInput(input) //atualiza o teclado
      }
      this.$emit('input', input) //atualiza o v-model
      this.$emit('change', input) //emite o evento 'change'
    },
    onInputFocus(e) {
      if (!this.disableVirtualKeyboard) {
        this.openKeyboard()
      }
      this.$emit('focus', e) //emite o evento 'focus'
    },
    onInputBlur(e) {
      this.$emit('blur', e) //emite o evento 'blur'
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.simple-keyboard {
  max-width: 850px;
  display: none;
  transition: margin-top 0s ease-in-out 0.3s, opacity 0.3s ease-in-out;
}

.show-keyboard {
  /* opacity: 1; */
  /* margin-top: 0; */
  display: block;
}
</style>
